import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:grid md:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden md:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <Link data-testid="login-home-link" to="/" className="font-display font-black uppercase tracking-tighter text-2xl leading-none">
          Design My<br />Cashflow
        </Link>
        <div>
          <h2 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl">Build your kingdom</h2>
          <p className="font-mono-accent text-xs uppercase tracking-[0.2em] mt-6 opacity-80">Admin Control Room</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-background">
        <motion.form
          onSubmit={submit}
          data-testid="login-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="w-12 h-12 bg-primary flex items-center justify-center mb-8">
            <Lock size={20} className="text-primary-foreground" />
          </div>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl mb-2">Sign in</h1>
          <p className="text-muted-foreground text-sm mb-8">Access the content control room.</p>

          {error && (
            <div data-testid="login-error" className="border border-destructive/50 bg-destructive/10 text-destructive text-sm px-4 py-3 mb-6">
              {error}
            </div>
          )}

          <label className="block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-card border border-border focus:border-primary px-4 py-3 mb-5 outline-none transition-colors"
          />

          <label className="block font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-card border border-border focus:border-primary px-4 py-3 mb-8 outline-none transition-colors"
          />

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground font-display font-bold uppercase tracking-wide py-4 hover:bg-[#1AE39C] transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in..." : "Enter"}
          </button>

          <Link data-testid="login-back-site" to="/" className="block text-center mt-6 font-mono-accent text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-primary transition-colors">
            ← Back to site
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
